import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';

import { userSelector } from '../../reducers/current-user-selectors';
import { accountSelector, stripeBasePlanSelector } from '../account-selectors';
import { cancelSubscription } from '../actions/cancel-subscription';
import { getStripeBasePlans } from '../actions/get-stripe-base-plans';
import { handleAnnualSubscriptionRenewalResponse } from '../actions/handle-annual-subscription-renewal-response';
import { loadAccount } from '../actions/load-account';

import * as Api from '../../api/api';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { UiRichTextEditor } from '../../ui/ui-rich-text-editor';

import { CancellationExplanation } from './cancellation-explanation';

import { richTextEditorConfig } from '../../util/ckeditor-config';
import { formatDate, currencyFormat } from '../../util/formatters';
import { confirm } from '../../widgets/confirmation-dialog';
import { LoadingMessage } from '../../widgets/loading-message';

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { accountId }: InputProps,
) => ({
  actions: {
    cancelSubscription: (cancelReason: string, cancelDetail: string) =>
      dispatch(cancelSubscription(accountId, cancelReason, cancelDetail)),
    handleAnnualSubscriptionRenewalResponse: (
      renewalResponse: 'renew' | 'change' | 'cancel',
      isChargeCreditCard: boolean,
    ) =>
      dispatch(
        handleAnnualSubscriptionRenewalResponse(accountId, renewalResponse, isChargeCreditCard),
      ),
    loadBasePlans: () => dispatch(getStripeBasePlans()),
    loadAccount: () => dispatch(loadAccount(accountId)),
  },
});

const mapStateToProps = (state: any, { accountId }: InputProps) => {
  const account = accountSelector(state, accountId);
  return {
    account,
    currentUser: userSelector(state),
    baseMonthlyRateStripePlan:
      account &&
      account.baseMonthlyRateStripePlanId &&
      stripeBasePlanSelector(state, account.baseMonthlyRateStripePlanId),
  };
};

type InputProps = {
  accountId: number;
  size: 'xs' | 'sm';
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const AnnualSubscriptionRenewalDialogButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ account, size, currentUser, actions, baseMonthlyRateStripePlan }: Props) => {
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [manageSubscriptionOption, setManageSubscriptionOption] = useState<string>('');
  const [annualRenewalOption, setAnnualRenewalOption] = useState<string>('');
  const [postCancelExplanationVariant, setPostCancelExplanationVariant] = useState<
    'success' | 'fail' | null
  >(null);
  const [cancelDetail, setCancelDetail] = useState<string>('');

  useEffect(() => {
    actions.loadAccount();
    actions.loadBasePlans();
  });

  /* Manage Subscription Options */
  const RENEW_OPTION = baseMonthlyRateStripePlan
    ? `Renew annual subscription (${currencyFormat(
        (baseMonthlyRateStripePlan.amount / 100) * 0.8 * 12,
      )} upfront, keep 20% off)`
    : 'Renew annual subscription (keep 20% off)';
  const SWITCH_TO_MONTHLY_OPTION = 'Switch to monthly';
  const CANCEL_OPTION = 'Cancel subscription';
  const MANAGE_SUBSCRIPTION_OPTIONS = [RENEW_OPTION, SWITCH_TO_MONTHLY_OPTION, CANCEL_OPTION];
  const manageSubscriptionOptions = Object.values(MANAGE_SUBSCRIPTION_OPTIONS).map((s) => ({
    label: s,
    value: s,
  }));

  /* Annual Renewal Billing Options */
  const CHARGE_CREDIT_CARD = 'Charge credit card';
  const INVOICE_SEPARATELY = 'Invoice separately';

  const toggleDialog = () => setDialogVisible(!dialogVisible);

  const handleSubmitClick = async () => {
    switch (manageSubscriptionOption) {
      case RENEW_OPTION:
        if (await confirm('Are you sure you would like to renew your subscription?')) {
          setSubmitting(true);
          await actions.handleAnnualSubscriptionRenewalResponse(
            'renew',
            annualRenewalOption === CHARGE_CREDIT_CARD,
          );
          toggleDialog();
          setSubmitting(false);
        }
        break;
      case SWITCH_TO_MONTHLY_OPTION:
        if (await confirm('Are you sure you would like to switch to a monthly subscription?')) {
          setSubmitting(true);
          await actions.handleAnnualSubscriptionRenewalResponse('change', false);
          toggleDialog();
          setSubmitting(false);
        }
        break;
      case CANCEL_OPTION:
        if (await confirm('Are you sure you would like to cancel your annual subscription?')) {
          await notifyCancellation();
        }
        break;
      default:
        break;
    }
  };

  const handleAnnualRenewalOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnnualRenewalOption(e.target.value);
  };

  const handleManageSubscriptionOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManageSubscriptionOption(e.target.value);
  };

  const notifyCancellation = async () => {
    setSubmitting(true);
    const response = await Api.post(`/account/${account.accountId}/cancel-subscription-request`, {
      body: JSON.stringify({ cancelDetail }),
    });
    const { success } = await response.json();
    setSubmitting(false);
    if (success) {
      setPostCancelExplanationVariant('success');
    } else {
      setPostCancelExplanationVariant('fail');
    }
  };

  const CancelComponent = () =>
    postCancelExplanationVariant && (
      <CancellationExplanation variant={postCancelExplanationVariant} />
    );

  const SwitchToMonthlyComponent = () =>
    account && (
      <div>
        Starting {formatDate(account.nextBaseSubscriptionBillingDate, 'MMMM Do, YYYY')}, your annual
        subscription will end, and this account will switch to a monthly subscription
        {baseMonthlyRateStripePlan && (
          <>
            {' '}
            at <strong>{currencyFormat(baseMonthlyRateStripePlan.amount / 100)}/month</strong>
          </>
        )}
        .
      </div>
    );

  const RenewAnnualComponent = () =>
    account && (
      <div>
        <FormControl component="fieldset">
          <div className="typo-label">Select a Billing Method</div>
          <RadioGroup
            name="radio-buttons-group"
            value={annualRenewalOption}
            onChange={handleAnnualRenewalOptionChange}
          >
            {baseMonthlyRateStripePlan && (
              <FormControlLabel
                value={CHARGE_CREDIT_CARD}
                control={<Radio />}
                label={`${CHARGE_CREDIT_CARD} - the card on file will be charged on ${formatDate(
                  account.nextBaseSubscriptionBillingDate,
                  'MM/DD/YY',
                )}`}
              />
            )}
            <FormControlLabel
              value={INVOICE_SEPARATELY}
              control={<Radio />}
              label={`${INVOICE_SEPARATELY} - an invoice will be sent to your email address`}
            />
          </RadioGroup>
        </FormControl>
      </div>
    );

  if (!currentUser.isAdmin && !currentUser.isAccountAdmin) {
    return null;
  }

  const handleCancelDetailTextChange = (e: any) => setCancelDetail(e.editor.getData());

  return (
    <>
      <UiButton variant="primary" size={size} onClick={toggleDialog} disabled={dialogVisible}>
        Renew or Cancel
      </UiButton>
      {dialogVisible && (
        <UiDialog title="Renew Your Annual Subscription" size="sm">
          <UiDialog.Content>
            {account ? (
              <>
                <div className="typo-h5">Choose an option</div>
                <div className="u-padding-top-sm">
                  <FormControl component="fieldset">
                    <div className="u-margin-y-sm">
                      {account.nextBaseSubscriptionBillingDate && (
                        <>
                          Your subscription is paid until{' '}
                          {formatDate(account.nextBaseSubscriptionBillingDate, 'MMMM Do, YYYY')}.
                        </>
                      )}{' '}
                      Please select one of the following options to either renew or cancel your
                      subscription.
                    </div>
                    <RadioGroup
                      name="radio-buttons-group"
                      value={manageSubscriptionOption}
                      onChange={handleManageSubscriptionOptionChange}
                    >
                      {manageSubscriptionOptions.map((mso) => (
                        <FormControlLabel
                          value={mso.value}
                          control={<Radio />}
                          label={mso.label}
                          key={`opt-${mso.value}`}
                          disabled={
                            manageSubscriptionOption === CANCEL_OPTION &&
                            !!postCancelExplanationVariant
                          }
                        />
                      ))}
                    </RadioGroup>
                    {manageSubscriptionOption === CANCEL_OPTION && !postCancelExplanationVariant ? (
                      <div className="u-margin-y-md">
                        <div>
                          Please let us know the reason you are cancelling your subscription:
                        </div>
                        <UiRichTextEditor
                          data={cancelDetail}
                          onChange={handleCancelDetailTextChange}
                          config={richTextEditorConfig}
                        />
                      </div>
                    ) : (
                      ''
                    )}
                  </FormControl>
                </div>
                <Divider variant="middle" />
                <div className="u-margin-y-md">
                  {manageSubscriptionOption === RENEW_OPTION && <RenewAnnualComponent />}
                  {manageSubscriptionOption === SWITCH_TO_MONTHLY_OPTION && (
                    <SwitchToMonthlyComponent />
                  )}
                  {manageSubscriptionOption === CANCEL_OPTION && <CancelComponent />}
                </div>
              </>
            ) : (
              <LoadingMessage />
            )}
          </UiDialog.Content>
          <UiDialog.Actions>
            {manageSubscriptionOption === CANCEL_OPTION && !!postCancelExplanationVariant ? (
              <UiButton onClick={toggleDialog} size="sm" disabled={!postCancelExplanationVariant}>
                Close Dialog
              </UiButton>
            ) : (
              <div className="u-spread">
                <UiButton variant="secondary" onClick={toggleDialog} className="u-margin-sm">
                  Cancel
                </UiButton>
                <UiButton
                  className="u-margin-sm"
                  variant="primary"
                  onClick={handleSubmitClick}
                  disabled={
                    submitting ||
                    !manageSubscriptionOption ||
                    (manageSubscriptionOption === RENEW_OPTION &&
                      annualRenewalOption.length === 0) ||
                    (manageSubscriptionOption === CANCEL_OPTION && !cancelDetail)
                  }
                >
                  {submitting && <CircularProgress className="u-margin-right-md" size={20} />}{' '}
                  Submit
                </UiButton>
              </div>
            )}
          </UiDialog.Actions>
        </UiDialog>
      )}
    </>
  );
});
